// One entry shell for the auth door, verification, recovery, invitation and the
// welcome steps (DESIGN-SPEC §6.19): a 56 px top bar with the Spark and wordmark
// on the left and one text action on the right, over a top-anchored column so
// a new message grows the form downward instead of moving the page.
import type { MouseEvent, ReactNode, Ref } from 'react';
import { LoaderCircleIcon, XIcon } from 'lucide-react';
import { Spark } from '@/components/spark';

export function EntryShell({
  layer,
  width = 'auth',
  rootRef,
  action,
  close,
  wash = false,
  testId,
  children,
}: {
  layer: 'auth' | 'welcome';
  width?: 'auth' | 'welcome';
  rootRef?: Ref<HTMLDivElement>;
  /** The one text action on the right of the top bar. */
  action?: { label: string; onClick: () => void; disabled?: boolean } | null;
  /** Present only when the screen can be dismissed (an authenticated recovery). */
  close?: { label: string; onClick: () => void } | null;
  wash?: boolean;
  testId?: string;
  children: ReactNode;
}) {
  // The scroll container's bottom padding rides --keyboard-inset (published by
  // useKeyboardInset in the chat pane) so an overlaying soft keyboard shrinks
  // the scroll box and the form scrolls above it instead of being occluded
  // behind it (iOS Safari and in-app WebViews never resize the layout). The
  // matching scroll-padding keeps native focus-reveal above the keyboard too.
  // The fade-in and the padding transition live in entry.css on the motion
  // tokens, so reduced motion reaches the shell itself.
  return (
    <div
      ref={rootRef}
      className="pc-entry pb-[calc(env(safe-area-inset-bottom)+var(--keyboard-inset,0px))] scroll-pb-[calc(env(safe-area-inset-bottom)+var(--keyboard-inset,0px))]"
      data-layer={layer}
      data-width={width}
      data-testid={testId}
    >
      <header className="pc-entry-bar">
        {/* Chrome recedes: a still mark here. The waking Spark belongs to the
            chat-home greeting, not to the bar beside a form. */}
        <span className="pc-entry-brand">
          <Spark size={20} />
          <span className="pc-entry-wordmark">poneva</span>
        </span>
        <span className="pc-entry-bar-actions">
          {action ? (
            <button
              type="button"
              className="pc-entry-bar-action"
              onClick={action.onClick}
              disabled={action.disabled}
            >
              {action.label}
            </button>
          ) : null}
          {close ? (
            <button
              type="button"
              className="pc-entry-close"
              onClick={close.onClick}
              aria-label={close.label}
              title={close.label}
            >
              <XIcon aria-hidden="true" />
            </button>
          ) : null}
        </span>
      </header>
      <main className="pc-entry-column">
        {wash ? <div aria-hidden className="brand-wash pc-entry-wash" /> : null}
        {children}
      </main>
    </div>
  );
}

/** Title and optional lead, centred; the title takes focus when a step changes. */
export function EntryHeading({
  title,
  lead,
  headingRef,
  tile,
}: {
  title: ReactNode;
  lead?: ReactNode;
  headingRef?: Ref<HTMLHeadingElement>;
  tile?: ReactNode;
}) {
  return (
    <div className="pc-entry-head">
      {tile ? <div className="pc-entry-tile">{tile}</div> : null}
      <h1 ref={headingRef} tabIndex={headingRef ? -1 : undefined} className="pc-entry-title">
        {title}
      </h1>
      {lead ? <p className="pc-entry-lead">{lead}</p> : null}
    </div>
  );
}

/**
 * The entry button (the lg slice of .pc-btn). Busy keeps the label and adds a
 * spinner, so the button never collapses to "…". A form's submit button runs
 * `onClick` and prevents the native submission, so Enter in a field (which
 * clicks the default button) and a pointer click take the same single path.
 */
export function EntryButton({
  variant = 'primary',
  block = false,
  busy = false,
  busyLabel,
  disabled,
  onClick,
  type = 'button',
  children,
}: {
  variant?: 'primary' | 'secondary' | 'quiet';
  block?: boolean;
  busy?: boolean;
  busyLabel?: string;
  disabled?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit';
  children: ReactNode;
}) {
  return (
    <button
      type={type}
      className="pc-btn"
      data-variant={variant}
      data-size="lg"
      data-block={block || undefined}
      aria-busy={busy || undefined}
      disabled={busy || disabled}
      onClick={onClick}
    >
      {busy ? (
        <LoaderCircleIcon className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
      ) : null}
      {busy && busyLabel ? busyLabel : children}
    </button>
  );
}
